// SPDX-License-Identifier: AGPL-3.0-or-later
// SPDX-FileCopyrightText: 2026 Ben Richardson <hi@ben.gy>
/** Pure analytical helpers over the name dataset. Shared with the test suite. */
import type { NameRec, SeriesKey } from './types';

/** rank of a name in a series at a given year, or null. */
export function rankAt(n: NameRec, sk: SeriesKey, year: number): number | null {
  const arr = n.series[sk];
  if (!arr) return null;
  for (const p of arr) if (p[0] === year) return p[1];
  return null;
}

export function countAt(n: NameRec, sk: SeriesKey, year: number): number | null {
  const arr = n.series[sk];
  if (!arr) return null;
  for (const p of arr) if (p[0] === year) return p[2];
  return null;
}

/** Every year present in a series across all names, ascending. */
export function yearsIn(names: NameRec[], sk: SeriesKey): number[] {
  const set = new Set<number>();
  for (const n of names) for (const p of n.series[sk] || []) set.add(p[0]);
  return [...set].sort((a, b) => a - b);
}

export interface Mover {
  name: string;
  key: string;
  from: number | null; // rank in the earlier year (null = outside top list)
  to: number | null; // rank in the later year
  delta: number; // positive = climbed (rank got smaller / entered); negative = fell
}

/**
 * Rank movement between two years within one series. A name outside the top list
 * in a year is treated as `outRank` (default 101) for the purpose of computing a
 * delta, so "entered from nowhere" and "dropped out" both score. Only names
 * present in at least one of the two years are returned.
 */
export function movers(names: NameRec[], sk: SeriesKey, fromYear: number, toYear: number, outRank = 101): Mover[] {
  const out: Mover[] = [];
  for (const n of names) {
    const from = rankAt(n, sk, fromYear);
    const to = rankAt(n, sk, toYear);
    if (from == null && to == null) continue;
    const f = from ?? outRank;
    const t = to ?? outRank;
    const delta = f - t; // rank decreased → positive climb
    if (delta === 0) continue;
    out.push({ name: n.n, key: n.k, from, to, delta });
  }
  return out.sort((a, b) => b.delta - a.delta);
}

/** Top-K climbers and fallers from a movers list. */
export function splitMovers(all: Mover[], k = 12): { risers: Mover[]; fallers: Mover[] } {
  const risers = all.filter((m) => m.delta > 0).slice(0, k);
  const fallers = all
    .filter((m) => m.delta < 0)
    .sort((a, b) => a.delta - b.delta)
    .slice(0, k);
  return { risers, fallers };
}

/**
 * The set of names that reached rank ≤ maxRank at least once in the series within
 * [minYear, maxYear], each with its rank at every year (null when it wasn't ranked
 * that year, or was worse than displayCap). Sorted by best (lowest) rank. Feeds
 * the bump chart.
 */
export interface BumpEntry {
  key: string;
  name: string;
  ranks: Map<number, number>; // year -> rank (only where ranked)
  best: number;
}

export function bumpEntries(
  names: NameRec[],
  sk: SeriesKey,
  years: number[],
  maxRank: number,
  displayCap = 25,
): BumpEntry[] {
  const yearSet = new Set(years);
  const out: BumpEntry[] = [];
  for (const n of names) {
    const arr = n.series[sk];
    if (!arr) continue;
    const ranks = new Map<number, number>();
    let best = Infinity;
    let qualifies = false;
    for (const [y, r] of arr) {
      if (!yearSet.has(y)) continue;
      if (r <= displayCap) ranks.set(y, r);
      if (r <= maxRank) qualifies = true;
      if (r < best) best = r;
    }
    if (qualifies && ranks.size > 0) out.push({ key: n.k, name: n.n, ranks, best });
  }
  return out.sort((a, b) => a.best - b.best);
}

/** Flash-in-the-pan: high single-year peak, few years in any top list. */
export function flashNames(names: NameRec[], { maxYears = 8, minPeak = 100 } = {}): Array<{ name: string; key: string; peak: number; years: number; peakYear: number }> {
  const out: Array<{ name: string; key: string; peak: number; years: number; peakYear: number }> = [];
  for (const n of names) {
    let years = 0;
    let peak = 0;
    let peakYear = 0;
    for (const sk of Object.keys(n.series) as SeriesKey[]) {
      const arr = n.series[sk] || [];
      years += arr.length;
      for (const [y, , c] of arr)
        if (c > peak) {
          peak = c;
          peakYear = y;
        }
    }
    if (years <= maxYears && peak >= minPeak) out.push({ name: n.n, key: n.k, peak, years, peakYear });
  }
  return out.sort((a, b) => b.peak - a.peak);
}

/** Histogram bins over a set of values (pure — layout-testable). */
export function histogramBins(values: number[], binCount: number): { lo: number; hi: number; count: number }[] {
  const vals = values.filter((v) => Number.isFinite(v));
  if (vals.length === 0 || binCount < 1) return [];
  const min = Math.min(...vals);
  const max = Math.max(...vals);
  if (min === max) return [{ lo: min, hi: max, count: vals.length }];
  const width = (max - min) / binCount;
  const bins = Array.from({ length: binCount }, (_, i) => ({ lo: min + i * width, hi: min + (i + 1) * width, count: 0 }));
  for (const v of vals) {
    let idx = Math.floor((v - min) / width);
    if (idx >= binCount) idx = binCount - 1;
    if (idx < 0) idx = 0;
    bins[idx].count++;
  }
  return bins;
}
